import React from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { Box, Table, TableBody } from '@mui/material';
import { SkeletonTableRow } from 'components/Common/SkeletonTableRow';
import { DataTable } from 'components/DataTable/DataTable';
import { BrVersionDto } from 'views/RevisionHistory/Br/BrRevisionDtos';
import { brRevisionHistoryHeadcells } from 'views/RevisionHistory/Br/BrRevisionHistoryHeadcells';
import { useFetchBrComparison } from 'hooks/queries/useFetchBrComparison';
import { RevisionDetail, RevisionDetailsError } from 'views/RevisionHistory/RevisionDtos';
import { filterByRegexAndSortRevisionHistoryEntries } from 'views/RevisionHistory/editHistoryHiddenAttributes';
import { closeModal, openModal } from 'reducers/modalReducer';
import { JBErrorViewHttpInformation } from '@bjb-gcrm/gcrm-frontend-components';

interface BrRevisionHistoryDetailTableProps {
  techId: string;
  brVersionDtos: BrVersionDto[];
  index: number;
}

export function BrRevisionHistoryDetailTable({ techId, brVersionDtos, index }: BrRevisionHistoryDetailTableProps) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = brVersionDtos[index];
  const previousHistory = brVersionDtos[index + 1];

  const oldVersion = previousHistory?.version ?? null;
  const newVersion = history.version;

  const {
    data,
    isFetching,
    isError,
    error: comparisonError,
  } = useFetchBrComparison(techId, previousHistory?.version ?? null, history.version);

  const EDIT_HISTORY_HIDDEN_ATTRIBUTES_REGEX = [/questionType$/, /question$/, /businessRelationshipType$/];

  const sortedData: RevisionDetail[] = isError
    ? [
        {
          section: 'All sections',
          fieldNameTranslation: '',
          fieldName: '',
          error: {
            message: `Error encountered while fetching comparison between version ${oldVersion} and ${newVersion}: ${comparisonError?.message}`,
            code: comparisonError?.response?.status ?? -1,
            cause: comparisonError?.response,
          } as RevisionDetailsError,
        },
      ]
    : filterByRegexAndSortRevisionHistoryEntries(EDIT_HISTORY_HIDDEN_ATTRIBUTES_REGEX, data);

  const onCloseErrorClick = () => {
    dispatch(closeModal('JBErrorModalWrapper'));
  };

  const onOpenErrorClick = (row: RevisionDetail) => {
    const title = t('revisionHistory.table.error.title');
    const userErrorMessage = t('revisionHistory.table.error.message', { sectionName: row.section });
    const httpInfo: JBErrorViewHttpInformation = {
      ...row.error,
      requestUrl: '',
      requestMethod: 'GET',
      responseStatus: row.error?.code ?? -1,
    };
    dispatch(
      openModal({
        modalType: 'JBErrorModalWrapper',
        modalProps: {
          technicalInfo: { httpInfo, details: row.error?.message, title, userErrorMessage },
          handleClose: onCloseErrorClick,
        },
      }),
    );
  };

  return (
    <div>
      <Box sx={{ width: '100%', mb: 2 }}>
        {isFetching ? (
          <Table>
            <TableBody>
              <SkeletonTableRow rowCount={5} cellCount={6} id="detailed-history-skeleton" />
            </TableBody>
          </Table>
        ) : (
          <DataTable
            id="revision-history-table"
            headCells={brRevisionHistoryHeadcells(onOpenErrorClick)}
            rows={sortedData ?? []}
          />
        )}
      </Box>
    </div>
  );
}
